import type { IChatToolCallHint } from '@/apiClient/types/chat'
import type { IChatToolCallEvent } from '@/composables/useEventStream'

/**
 * Folds one `mcp_tool_call_start` / `mcp_tool_call_end` event into the reply's tool calls,
 * the same way the API builds the `ui_hints.mcp_tool_calls` it saves: a start appends a row,
 * and an end settles the latest still-started row for that tool. That keeps the streamed list
 * identical to the one `message_complete` brings back. Returns a new array.
 */
export function applyToolCallEvent(
  calls: IChatToolCallHint[],
  event: IChatToolCallEvent,
): IChatToolCallHint[] {
  const {
    connector,
    tool_name,
    status,
    description,
    error,
    reasoning_offset,
    results,
  } = event
  if (status === 'started') {
    return [
      ...calls,
      { connector, tool_name, status, description, reasoning_offset },
    ]
  }

  const startedIndex = calls.findLastIndex(
    (call) =>
      call.connector === connector &&
      call.tool_name === tool_name &&
      call.status === 'started',
  )
  // An end with no start to settle (its start was missed) keeps any reasoning offset it
  // carries, so the row is still placed among the thoughts rather than after them all.
  if (startedIndex === -1) {
    return [
      ...calls,
      {
        connector,
        tool_name,
        status,
        description,
        error,
        reasoning_offset,
        results,
      },
    ]
  }

  return calls.map((call, index) =>
    index === startedIndex
      ? {
          ...call,
          status,
          error: error ?? call.error,
          description: call.description || description,
          results: results ?? call.results,
        }
      : call,
  )
}
